import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button } from "../primitives";
import "./walkthrough-overlay.css";

const TOOLTIP_WIDTH = 360;
const TOOLTIP_GAP = 12;
const TOOLTIP_MARGIN = 16;
const SPOTLIGHT_PADDING = 10;
const INTERACTION_PADDING = 6;
const SPOTLIGHT_RADIUS_PX = 20;
const SCROLL_LOCK_KEYS = new Set([
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "PageUp",
  "PageDown",
  "Home",
  "End",
  " ",
]);

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function parseRadiusValue(value) {
  const numeric = Number.parseFloat(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function getElementRadius(element) {
  if (!element || typeof window === "undefined") return SPOTLIGHT_RADIUS_PX;
  const styles = window.getComputedStyle(element);
  const radii = [
    styles.borderTopLeftRadius,
    styles.borderTopRightRadius,
    styles.borderBottomRightRadius,
    styles.borderBottomLeftRadius,
  ].map(parseRadiusValue);
  const resolvedRadius = Math.max(...radii, 0);
  return resolvedRadius || SPOTLIGHT_RADIUS_PX;
}

function buildRoundedRectPath(rect, radius) {
  const right = rect.left + rect.width;
  const bottom = rect.top + rect.height;
  return [
    `M ${rect.left + radius} ${rect.top}`,
    `H ${right - radius}`,
    `A ${radius} ${radius} 0 0 1 ${right} ${rect.top + radius}`,
    `V ${bottom - radius}`,
    `A ${radius} ${radius} 0 0 1 ${right - radius} ${bottom}`,
    `H ${rect.left + radius}`,
    `A ${radius} ${radius} 0 0 1 ${rect.left} ${bottom - radius}`,
    `V ${rect.top + radius}`,
    `A ${radius} ${radius} 0 0 1 ${rect.left + radius} ${rect.top}`,
    "Z",
  ].join(" ");
}

function buildOverlayPath(rect, viewport, radius) {
  const outerPath = `M 0 0 H ${viewport.width} V ${viewport.height} H 0 Z`;
  const innerPath = buildRoundedRectPath(rect, radius);
  return `${outerPath} ${innerPath}`;
}

function getRectForElement(element) {
  if (!element) return null;
  const rect = element.getBoundingClientRect();
  if (!rect || rect.width === 0 || rect.height === 0) return null;
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    right: rect.right,
    bottom: rect.bottom,
    radius: getElementRadius(element),
  };
}

function expandRect(rect, viewport, padding = SPOTLIGHT_PADDING) {
  if (!rect) return null;
  const top = clamp(rect.top - padding, 0, viewport.height);
  const left = clamp(rect.left - padding, 0, viewport.width);
  const right = clamp(rect.right + padding, 0, viewport.width);
  const bottom = clamp(rect.bottom + padding, 0, viewport.height);
  return {
    top,
    left,
    right,
    bottom,
    width: Math.max(0, right - left),
    height: Math.max(0, bottom - top),
    radius: rect.radius + padding,
  };
}

function resolveTooltipPosition(
  rect,
  viewport,
  preferredPlacement,
  tooltipWidth = TOOLTIP_WIDTH
) {
  if (!rect) {
    return { top: TOOLTIP_MARGIN, left: TOOLTIP_MARGIN, placement: "bottom" };
  }
  const { width: viewportWidth, height: viewportHeight } = viewport;
  const estimatedHeight = 220;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  const spaceRight = viewportWidth - rect.right;
  const spaceLeft = rect.left;
  const preferBelow = spaceBelow >= estimatedHeight || spaceBelow >= spaceAbove;
  let placement = preferBelow ? "bottom" : "top";

  if (preferredPlacement === "right" && spaceRight >= tooltipWidth + TOOLTIP_MARGIN) {
    placement = "right";
  } else if (preferredPlacement === "left" && spaceLeft >= tooltipWidth + TOOLTIP_MARGIN) {
    placement = "left";
  }
  if (preferredPlacement === "top" && spaceAbove > TOOLTIP_MARGIN) {
    placement = "top";
  }
  if (preferredPlacement === "bottom" && spaceBelow > TOOLTIP_MARGIN) {
    placement = "bottom";
  }

  let left = clamp(
    rect.left,
    TOOLTIP_MARGIN,
    viewportWidth - tooltipWidth - TOOLTIP_MARGIN
  );
  let rawTop = preferBelow
    ? rect.bottom + TOOLTIP_GAP
    : rect.top - TOOLTIP_GAP - estimatedHeight;

  if (placement === "right") {
    left = clamp(
      rect.right + TOOLTIP_GAP,
      TOOLTIP_MARGIN,
      viewportWidth - tooltipWidth - TOOLTIP_MARGIN
    );
    rawTop = rect.top + rect.height / 2 - estimatedHeight / 2;
  } else if (placement === "left") {
    left = clamp(
      rect.left - tooltipWidth - TOOLTIP_GAP,
      TOOLTIP_MARGIN,
      viewportWidth - tooltipWidth - TOOLTIP_MARGIN
    );
    rawTop = rect.top + rect.height / 2 - estimatedHeight / 2;
  }

  const top = clamp(rawTop, TOOLTIP_MARGIN, viewportHeight - estimatedHeight - TOOLTIP_MARGIN);
  return { top, left, placement };
}

export default function WalkthroughOverlay({
  isOpen,
  step,
  stepIndex = 0,
  totalSteps = 0,
  onSkip,
  onNext,
}) {
  const [highlightRect, setHighlightRect] = useState(null);
  const [interactiveRect, setInteractiveRect] = useState(null);
  const [tooltipAnchorRect, setTooltipAnchorRect] = useState(null);
  const hasAutoScrolledStepRef = useRef("");
  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    if (!isOpen) {
      hasAutoScrolledStepRef.current = "";
      setHighlightRect(null);
      setInteractiveRect(null);
      setTooltipAnchorRect(null);
      return;
    }

    const syncTarget = () => {
      const nextViewport = {
        width: window.innerWidth,
        height: window.innerHeight,
      };
      setViewport(nextViewport);

      const highlightSelector = step?.highlightSelector || step?.selector || "";
      const actionSelector =
        step?.actionSelector || (step?.requiresAction ? highlightSelector : "");
      const tooltipAnchorSelector = step?.tooltipAnchorSelector || "";
      const scrollTargetSelector =
        step?.scrollTargetSelector || actionSelector || highlightSelector;

      if (!highlightSelector) {
        setHighlightRect(null);
        setInteractiveRect(null);
        setTooltipAnchorRect(null);
        return;
      }

      const highlightElement = document.querySelector(highlightSelector);
      const actionElement = actionSelector ? document.querySelector(actionSelector) : null;
      const tooltipAnchorElement = tooltipAnchorSelector
        ? document.querySelector(tooltipAnchorSelector)
        : null;
      const scrollTargetElement = scrollTargetSelector
        ? document.querySelector(scrollTargetSelector)
        : highlightElement;

      if (
        scrollTargetElement &&
        step.scrollIntoView !== false &&
        hasAutoScrolledStepRef.current !== String(step.id || "")
      ) {
        try {
          scrollTargetElement.scrollIntoView({
            behavior: "auto",
            block: step.scrollBlock || "center",
            inline: "nearest",
          });
        } catch {
          // Ignore scroll failures.
        }
        if (step.autoFocus && typeof scrollTargetElement.focus === "function") {
          try {
            scrollTargetElement.focus({ preventScroll: true });
          } catch {
            scrollTargetElement.focus();
          }
        }
        hasAutoScrolledStepRef.current = String(step.id || "");
      }

      setHighlightRect(getRectForElement(highlightElement));
      setInteractiveRect(getRectForElement(actionElement));
      setTooltipAnchorRect(getRectForElement(tooltipAnchorElement));
    };

    syncTarget();

    const observer = new MutationObserver(() => {
      syncTarget();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    window.addEventListener("resize", syncTarget);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", syncTarget);
    };
  }, [isOpen, step]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyTouchAction = document.body.style.touchAction;
    const previousHtmlOverflow = document.documentElement.style.overflow;
    const previousHtmlTouchAction = document.documentElement.style.touchAction;

    const preventScroll = (event) => {
      event.preventDefault();
    };

    const preventScrollKeys = (event) => {
      if (!SCROLL_LOCK_KEYS.has(event.key)) return;
      const target = event.target;
      const isInsideTooltip = target instanceof Element
        ? target.closest(".walkthroughOverlay__tooltip")
        : null;
      if (isInsideTooltip) return;
      event.preventDefault();
    };

    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.touchAction = "none";

    window.addEventListener("wheel", preventScroll, { passive: false });
    window.addEventListener("touchmove", preventScroll, { passive: false });
    window.addEventListener("keydown", preventScrollKeys, { passive: false });

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.touchAction = previousBodyTouchAction;
      document.documentElement.style.overflow = previousHtmlOverflow;
      document.documentElement.style.touchAction = previousHtmlTouchAction;
      window.removeEventListener("wheel", preventScroll);
      window.removeEventListener("touchmove", preventScroll);
      window.removeEventListener("keydown", preventScrollKeys);
    };
  }, [isOpen]);

  if (!isOpen || !step) return null;

  const stepCounter = totalSteps ? `${stepIndex + 1} of ${totalSteps}` : null;
  const description = step.description || "";
  const spotlightPadding = step?.spotlightPadding ?? SPOTLIGHT_PADDING;
  const interactionPadding = step?.interactionPadding ?? INTERACTION_PADDING;
  const tooltipWidth = Math.min(step?.tooltipWidth ?? TOOLTIP_WIDTH, viewport.width - TOOLTIP_MARGIN * 2);
  const spotlightRect = expandRect(highlightRect, viewport, spotlightPadding);
  const interactionRect = step.requiresAction
    ? expandRect(interactiveRect, viewport, interactionPadding)
    : null;
  const tooltipPosition = resolveTooltipPosition(
    tooltipAnchorRect || interactionRect || spotlightRect || highlightRect,
    viewport,
    step?.tooltipPlacement,
    tooltipWidth
  );
  const showContinueButton = !step.requiresAction;
  const continueLabel = step.primaryLabel || (stepIndex >= totalSteps - 1 ? "Finish" : "Continue");
  const blockerRect = interactionRect || spotlightRect;
  const cornerRadius = blockerRect
    ? Math.max(
        0,
        Math.min(
          blockerRect.radius || SPOTLIGHT_RADIUS_PX,
          blockerRect.width / 2,
          blockerRect.height / 2
        )
      )
    : 0;
  const blockerPath = blockerRect
    ? buildOverlayPath(blockerRect, viewport, cornerRadius)
    : "";

  const overlayContent = (
    <div className="walkthroughOverlay" role="dialog" aria-modal="true">
      {blockerRect ? (
        <>
          <svg
            className="walkthroughOverlay__svg"
            viewBox={`0 0 ${viewport.width} ${viewport.height}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              className="walkthroughOverlay__svgBlocker"
              d={blockerPath}
              fillRule="evenodd"
            />
          </svg>
          {!interactionRect ? (
            <div
              className="walkthroughOverlay__focusBlocker"
              style={{
                top: `${blockerRect.top}px`,
                left: `${blockerRect.left}px`,
                width: `${blockerRect.width}px`,
                height: `${blockerRect.height}px`,
              }}
            />
          ) : null}
        </>
      ) : (
        <div className="walkthroughOverlay__blocker walkthroughOverlay__blocker--full" />
      )}

      {spotlightRect ? (
        <div
          className="walkthroughOverlay__spotlight"
          style={{
            top: `${spotlightRect.top}px`,
            left: `${spotlightRect.left}px`,
            width: `${spotlightRect.width}px`,
            height: `${spotlightRect.height}px`,
            borderRadius: `${Math.min(
              spotlightRect.radius || SPOTLIGHT_RADIUS_PX,
              spotlightRect.width / 2,
              spotlightRect.height / 2
            )}px`,
          }}
        />
      ) : null}

      <div
        key={step.id || "walkthrough-tooltip"}
        className={`walkthroughOverlay__tooltip walkthroughOverlay__tooltip--${tooltipPosition.placement}`}
        style={{
          top: `${tooltipPosition.top}px`,
          left: `${tooltipPosition.left}px`,
          width: `${tooltipWidth}px`,
        }}
      >
        <div className="walkthroughOverlay__header">
          {stepCounter && <span className="walkthroughOverlay__counter">Step {stepCounter}</span>}
          <h3 className="walkthroughOverlay__title">{step.title}</h3>
        </div>
        <p className="walkthroughOverlay__description">{description}</p>
        {step.requiresAction ? (
          <p className="walkthroughOverlay__instruction">
            {step.actionLabel || "Use the highlighted control to continue."}
          </p>
        ) : null}

        <div className="walkthroughOverlay__actions">
          <Button variant="ghost" onClick={onSkip}>
            Skip
          </Button>
          {showContinueButton ? (
            <Button variant="primary" onClick={onNext}>
              {continueLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );

  return createPortal(overlayContent, document.body);
}
